import Phaser from 'phaser';
import { COLORS } from './theme';

export interface ArrowOptions {
  direction: 'left' | 'right';
  size?: number;
  fill?: number;
  stroke?: number;
  strokeWidth?: number;
  onClick?: () => void;
}

// Chunky pink directional arrow drawn from primitives so it stays sharp at
// any scale. Polygon is laid out so its visual bounds match container bounds
// exactly — the hit rectangle then aligns with the art.
export class Arrow extends Phaser.GameObjects.Container {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly baseScale = 1;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: ArrowOptions) {
    super(scene, x, y);
    scene.add.existing(this);

    const size = opts.size ?? 180;
    const fill = opts.fill ?? COLORS.accentPink;
    const stroke = opts.stroke ?? COLORS.white;
    const strokeWidth = opts.strokeWidth ?? 6;

    const w = size;
    const h = size * 0.6;
    const halfW = w / 2;
    const halfH = h / 2;
    const headWidth = w * 0.42;
    const shaftThickness = h * 0.46;
    const sign = opts.direction === 'right' ? 1 : -1;

    // Path: shaft (tail edge → head-back) + triangular head (back → tip → back).
    // For direction='right', shaft tail is at -halfW, tip is at +halfW.
    const tailX = -halfW * sign;
    const headBackX = (halfW - headWidth) * sign;
    const tipX = halfW * sign;

    const path = [
      { x: tailX,     y: -shaftThickness / 2 },
      { x: headBackX, y: -shaftThickness / 2 },
      { x: headBackX, y: -halfH },
      { x: tipX,      y: 0 },
      { x: headBackX, y: halfH },
      { x: headBackX, y: shaftThickness / 2 },
      { x: tailX,     y: shaftThickness / 2 },
    ];

    this.graphics = scene.add.graphics();
    this.add(this.graphics);
    this.graphics.lineStyle(strokeWidth, stroke, 1);
    this.graphics.fillStyle(fill, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(path[0].x, path[0].y);
    for (let i = 1; i < path.length; i++) this.graphics.lineTo(path[i].x, path[i].y);
    this.graphics.closePath();
    this.graphics.fillPath();
    this.graphics.strokePath();

    // Hit rectangle derived from the actual polygon bounds + half-stroke margin.
    const xs = path.map((p) => p.x);
    const ys = path.map((p) => p.y);
    const minX = Math.min(...xs) - strokeWidth / 2;
    const maxX = Math.max(...xs) + strokeWidth / 2;
    const minY = Math.min(...ys) - strokeWidth / 2;
    const maxY = Math.max(...ys) + strokeWidth / 2;
    const hitW = maxX - minX;
    const hitH = maxY - minY;

    this.setInteractive(new Phaser.Geom.Rectangle(minX, minY, hitW, hitH), Phaser.Geom.Rectangle.Contains);
    this.input!.cursor = 'pointer';
    // DEBUG: outline what Phaser uses as the hit rect. Remove once verified.
    if ((scene as unknown as { debugHit?: boolean }).debugHit) {
      const dbg = scene.add.graphics();
      this.add(dbg);
      dbg.lineStyle(2, 0x00ff00, 1);
      dbg.strokeRect(minX, minY, hitW, hitH);
      scene.input.enableDebug(this, 0xffff00);
    }

    this.on('pointerover', () => this.setScale(this.baseScale * 1.08));
    this.on('pointerout',  () => this.setScale(this.baseScale));
    this.on('pointerdown', () => this.setScale(this.baseScale * 0.96));
    this.on('pointerup',   () => {
      this.setScale(this.baseScale * 1.08);
      opts.onClick?.();
    });
  }
}
